import { formatDate } from '@frontend/mwm-ui'
import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import * as Yup from 'yup'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { USER_TYPE } from '~/types/app-user'
import type { DeviceType } from '~/types/device-type'

import getColumns from './columns'

import AlgoliaTable from '~/components/Algolia/Table'
import Button from '~/components/Buttons/Button'
import DateInput from '~/components/Inputs/ReactHookForm/DateInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import { DEVICE_TYPE, FORM_ERRORS, TODAY } from '~/const'
import { ALGOLIA_INDEX_NAMES } from '~/const/algolia'
import { getDeviceTypeOptions } from '~/const/options'
import useApps from '~/hooks/useApps'
import { useCreateAppUserPurchaseMutation } from '~/queries/app-purchases'
import { keys as appsUsersKeys } from '~/queries/app-users'
import { IAP_TYPE } from '~/types/iap'
import { getIapFilters } from '~/utils/algolia'
import { isFocusedCellActions } from '~/utils/table'

import './styles.scss'

const ENTRIES_NAME = 'create-purchase-iaps'

interface Props {
  env: USER_TYPE
  onClosePanel: VoidFunction
  onFailure: (message: string) => void
  onSuccess: (message: string) => void
  userId?: string
}

interface FormValues {
  appKey: string
  deviceType: DeviceType
  expirationDate?: string
  sku: string
  type: IAP_TYPE
}

const validationSchema = Yup.object().shape({
  appKey: Yup.string().required(FORM_ERRORS.REQUIRED),
  deviceType: Yup.mixed<DeviceType>().oneOf(Object.values(DEVICE_TYPE), FORM_ERRORS.REQUIRED).required(),
  expirationDate: Yup.string().when('type', {
    is: (value: IAP_TYPE) => value === IAP_TYPE.SUBSCRIPTION,
    then: (schema) => schema.required(FORM_ERRORS.REQUIRED),
  }),
  sku: Yup.string().required(),
  type: Yup.mixed<IAP_TYPE>().oneOf(Object.values(IAP_TYPE), FORM_ERRORS.REQUIRED).required(),
})

const defaultValues = {
  appKey: '',
  deviceType: DEVICE_TYPE.IOS,
  expirationDate: '',
  sku: '',
  type: IAP_TYPE.SUBSCRIPTION,
}

const deviceTypeOptions = getDeviceTypeOptions(['android', 'ios', 'web'])

const iapTypeOptions = [
  { label: 'Product', value: 'product' },
  { label: 'Subscription', value: 'subscription' },
]

const formattedTodayDate = formatDate(TODAY, 'YYYY-MM-DD')

const PanelContent = ({ env, onClosePanel, onFailure, onSuccess, userId }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  /** Queries & Mutations */

  const { appKeys, isAppsLoading } = useApps()

  const appKeyOptions = appKeys.map(({ appKey, url }) => ({ label: appKey, url, value: appKey }))

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = useForm<FormValues>({ defaultValues, resolver: yupResolver(validationSchema) })

  const appKeyWatch = watch('appKey')

  const deviceTypeWatch = watch('deviceType')

  const iapTypeWatch = watch('type')

  const isTableVisible = !!appKeyWatch && !!deviceTypeWatch && !!iapTypeWatch

  const isSubmitButtonDisabled = !isValid || isSubmitting

  const configurationFilters = useMemo(
    () =>
      appKeyWatch &&
      deviceTypeWatch &&
      getIapFilters({
        appKey: appKeyWatch,
        deviceType: deviceTypeWatch,
      }),
    [appKeyWatch, deviceTypeWatch]
  )

  const { mutateAsync: createPurchase } = useCreateAppUserPurchaseMutation()

  /** Handlers */

  const handleRowClicked = (evt: RowClickedEvent) => {
    if (!isFocusedCellActions(evt)) {
      const { data } = evt
      setValue('sku', data.sku, { shouldDirty: true, shouldTouch: true })
      trigger()
    }
  }

  const getDeviceTypeDefaultOption = () => deviceTypeOptions.find((option) => option.value === deviceTypeWatch)

  const getIapTypeDefaultOption = () => iapTypeOptions.find((option) => option.value === iapTypeWatch)

  const getAlgoliaIndexName = () =>
    iapTypeWatch === 'subscription' ? ALGOLIA_INDEX_NAMES.IAP_SUBSCRIPTIONS : ALGOLIA_INDEX_NAMES.IAP_PRODUCTS

  const handleDeviceTypeSelectChange = (selectedOption: IOption | null, field: 'appKey' | 'deviceType' | 'type') => {
    setValue(field, selectedOption?.value || '', { shouldDirty: true, shouldTouch: true })
    appKeyWatch && trigger()
  }

  const handleDateChange = (date?: string) => {
    date && setValue('expirationDate', date, { shouldDirty: true, shouldTouch: true })
    trigger()
  }

  const handleFormSubmit = async (values: FormValues) => {
    if (userId && values && isValid) {
      try {
        await createPurchase({
          ...values,
          env,
          userId,
        })
        queryClient.invalidateQueries({ queryKey: appsUsersKeys.details(env, userId) })
        onSuccess('Purchase successfully created')
      } catch (e) {
        onFailure('Purchase could not be created')
      }
    }
  }

  return (
    <div className="create-user-purchase-panel">
      <div className="create-user-purchase-panel__header">
        <form
          className="create-user-purchase-panel__header__create-purchase-form"
          id="create-user-purchase-form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Controller
            control={control}
            name="appKey"
            render={({ field }) => (
              <SelectInput
                {...field}
                components={{ Option: CustomAppOption }}
                error={errors.appKey?.message}
                isLoading={isAppsLoading}
                label="App key"
                onChange={(option) => handleDeviceTypeSelectChange(option as IOption | null, 'appKey')}
                options={appKeyOptions}
                placeholder="Select an app key"
                required
                value={appKeyOptions?.find(({ value }) => value === field.value)}
              />
            )}
          />
          <Controller
            control={control}
            name="deviceType"
            render={({ field }) => (
              <SelectInput
                {...field}
                defaultValue={getDeviceTypeDefaultOption}
                error={errors.deviceType?.message}
                label="Device type"
                onChange={(option) => handleDeviceTypeSelectChange(option as IOption | null, 'deviceType')}
                options={deviceTypeOptions}
                placeholder="Select a device type"
                required
                value={deviceTypeOptions?.find(({ value }) => value === field.value)}
              />
            )}
          />
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <SelectInput
                {...field}
                defaultValue={getIapTypeDefaultOption()}
                error={errors.type?.message}
                label="Type"
                onChange={(option) => handleDeviceTypeSelectChange(option as IOption | null, 'type')}
                options={iapTypeOptions}
                placeholder="Select an IAP type"
                required
                value={iapTypeOptions?.find(({ value }) => value === field.value)}
              />
            )}
          />
          <Controller
            control={control}
            name="expirationDate"
            render={() => (
              <DateInput
                disabled={iapTypeWatch === IAP_TYPE.PRODUCT}
                error={errors.expirationDate?.message}
                label="Expiration date"
                minDate={TODAY}
                name="expirationDate"
                onDateChange={handleDateChange}
                placeholder={formattedTodayDate}
                required={iapTypeWatch === IAP_TYPE.SUBSCRIPTION}
              />
            )}
          />
        </form>
      </div>
      <div className="create-user-purchase-panel__table-wrapper">
        {isTableVisible ? (
          <AlgoliaTable
            className="with-min-height"
            columnDefs={getColumns(deviceTypeWatch)}
            configurationFilters={configurationFilters}
            entriesName={ENTRIES_NAME}
            hitsPerPage={10}
            indexName={getAlgoliaIndexName()}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowSelection={{
              enableClickSelection: true,
              mode: 'singleRow',
            }}
            tableRef={tableRef}
          />
        ) : null}
      </div>
      <div className="create-user-purchase-panel__actions-container">
        <Button ghost onClick={onClosePanel} type="button">
          Cancel
        </Button>
        <Button disabled={isSubmitButtonDisabled} form="create-user-purchase-form" type="submit">
          Save
        </Button>
      </div>
      <DevTool control={control} />
    </div>
  )
}

export default PanelContent
